import { Button, Heading, ISelectOption } from '@salecycle/ui-component-library';
import useNotifications from 'core/notifications/useNotifications';
import { ITemplateLanguageStatusEnum } from 'entities/template/template';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath, useHistory, useParams, useLocation } from 'react-router';
import { TEMPLATE_STATUS_COLOURS } from 'sections/design/template-management/constants/constants';
import { TemplateManagementRoute } from 'sections/design/template-management/routes';
import { TemplateButton } from '../../template-list/components/styles';
import { getEditRoute } from '../../template-list/components/SelectedTemplateSidebar/utils/getEditRoute';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { saveTemplate } from '../utils/queries';
import { TemplateEditorUnsavedChangesModal } from './modals/TemplateEditorUnsavedChangesModal';
import { NameBarElement, NameBarElementItem, NameBarRightElement } from './style';
import { TemplateLanguageSelect } from './TemplateLanguageSelect';
import { useFeatureFlags } from 'core/app/AppContext';

export const TemplateEditorNameBar = () => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages']);
  const { siteId, id: templateId, language } = useParams<{ siteId: string; id: string; language: string }>();
  const { template, hasUnsavedChanges, languageStatus } = useTemplateEditorState();
  const { push } = useHistory();
  const [setUnsavedChangesModalOpen, setUnsavedChangesModelOpen] = useState(false);
  const [templateLanguageStatus, setTemplateLanguageStatus] = useState(languageStatus);
  const [redirectUrl, setRedirectUrl] = useState('');
  const { mutate } = useMutation(saveTemplate);
  const { showNotification } = useNotifications();
  const dispatch = useTemplateEditorDispatch();
  const { pathname } = useLocation();
  const onsiteTemplate = pathname.includes('onsite');
  const { flagEnabled } = useFeatureFlags();

  const onLanguageChange = (option: ISelectOption<string>) => {
    const editRoute = onsiteTemplate ? TemplateManagementRoute.EDIT_ONSITE : getEditRoute(template);

    if (hasUnsavedChanges) {
      setRedirectUrl(generatePath(editRoute, { siteId, id: templateId, language: option.value }));
      setUnsavedChangesModelOpen(true);
    } else {
      push(generatePath(editRoute, { siteId, id: templateId, language: option.value }));
      setTemplateLanguageStatus(languageStatus);
    }
  };

  const handleConfirm = () => {
    mutate(
      {
        siteId,
        templateId,
        templateName: template.templateName,
        templateBody: template.template,
        enrichmentConfigIds: template.enrichmentConfigIds,
        language,
        languageStatus: ITemplateLanguageStatusEnum.Live,
        comment: `${language} - ${ITemplateLanguageStatusEnum.Live}`,
        type: template.type,
        t,
      },
      {
        onSuccess: () => {
          showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateSuccess'), 'normal');
          setTemplateLanguageStatus(ITemplateLanguageStatusEnum.Live);
          dispatch({
            type: 'SAVE_TEMPLATE',
          });
        },
        onError: () => {
          showNotification(t('designs:designGallery.form.comment.notifications.saveTemplateError'), 'error');
        },
      },
    );
  };

  const handleOpenCopyEditor = useCallback(
    () => window.open(`/sites/${siteId}/designs/templates/${templateId}/${language}/copy-editor/`, '_self'),
    [siteId, templateId, language],
  );

  useEffect(() => {
    setTemplateLanguageStatus(languageStatus);
  }, [languageStatus]);

  return (
    <>
      <NameBarElement data-testid="TemplateNameBar">
        <NameBarElementItem>
          <Heading variation="h4">{template.templateName}</Heading>
        </NameBarElementItem>
        <NameBarElementItem minWidth="110px">
          <TemplateLanguageSelect onLanguageChange={onLanguageChange} />
        </NameBarElementItem>
        {templateLanguageStatus === ITemplateLanguageStatusEnum.Live ? (
          <TemplateButton backgroundColor={TEMPLATE_STATUS_COLOURS['LIVE']}>
            {t('designs:designGallery.templateEditor.nameToolbar.templateLanguageStatusHeader.liveText')}
          </TemplateButton>
        ) : (
          <TemplateButton backgroundColor={TEMPLATE_STATUS_COLOURS['DRAFT']} onClick={handleConfirm}>
            {t('designs:designGallery.templateEditor.nameToolbar.goLiveButton.label')}
          </TemplateButton>
        )}
        {flagEnabled('ld-copy-editor') && (
          <NameBarRightElement>
            <Button data-testid="OpenCopyEditorButton" variation="primary" onClick={handleOpenCopyEditor}>
              {t('designs:designGallery.templateEditor.nameToolbar.templateCopyEditor.openCopyEditor')}
            </Button>
          </NameBarRightElement>
        )}
      </NameBarElement>
      <TemplateEditorUnsavedChangesModal
        unsavedChangesModalOpen={setUnsavedChangesModalOpen}
        setUnsavedChangesModalOpen={setUnsavedChangesModelOpen}
        redirectUrl={redirectUrl}
      />
    </>
  );
};
